/**
 * VIP OGLEDALA — Inspiration Gallery v3 JS
 * File: vip-inspiration.js
 * Design: Simple continuous scroll (NO snap), compact header, photo scroll arrows
 * Features:
 *   - IntersectionObserver fade-in for gallery items
 *   - Scroll state (.vip-insp--scrolled) tracks window.scroll (no snap container)
 *   - Load-more (hidden items revealed on button click)
 *   - Lightbox: horizontal carousel with translate3d, touch swipe L/R,
 *     prev/next arrows, keyboard ArrowLeft/Right, Esc/X/click-outside close,
 *     slide counter, body scroll lock
 * No external dependencies. Vanilla JS IIFE.
 */
(function () {
  'use strict';

  /* ---- Reduce motion check ---- */
  var prefersReducedMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init() {
    var root = document.querySelector('.vip-insp');
    if (!root) return;

    /* ---- Read config from data attributes ---- */
    var initialCount   = parseInt(root.dataset.inspInitial, 10) || 20;
    var enableLightbox = root.dataset.inspLightbox === 'true';
    var showLoadMore   = root.dataset.inspLoadMore === 'true';

    /* ----------------------------------------------------------------
       FADE-IN via IntersectionObserver
       Gallery is normal document flow — threshold 0.15 works well
    ---------------------------------------------------------------- */
    if (!prefersReducedMotion && 'IntersectionObserver' in window) {
      var fadeObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            fadeObserver.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.15
      });

      root.querySelectorAll('[data-insp-fade]').forEach(function (item) {
        fadeObserver.observe(item);
      });
    } else {
      /* Reduce motion or no IO support: show all immediately */
      root.querySelectorAll('[data-insp-fade]').forEach(function (item) {
        item.classList.add('is-visible');
      });
    }

    /* ----------------------------------------------------------------
       SCROLL STATE — window.scroll (no dedicated snap container)
       Adds .vip-insp--scrolled when user scrolls past section top + 50vh
    ---------------------------------------------------------------- */
    var halfVh  = window.innerHeight * 0.5;
    var scrolled = false;

    function onWindowScroll() {
      var y = window.scrollY || window.pageYOffset;
      if (y > halfVh && !scrolled) {
        root.classList.add('vip-insp--scrolled');
        scrolled = true;
      } else if (y <= halfVh && scrolled) {
        root.classList.remove('vip-insp--scrolled');
        scrolled = false;
      }
    }

    window.addEventListener('scroll', onWindowScroll, { passive: true });

    /* ----------------------------------------------------------------
       LOAD MORE — reveals hidden items in batches of 20
    ---------------------------------------------------------------- */
    var gallery  = root.querySelector('[data-insp-gallery]');
    var allItems = gallery
      ? Array.prototype.slice.call(gallery.querySelectorAll('.vip-insp__item'))
      : [];

    var shownCount  = 0;
    var loadMoreBtn = root.querySelector('.vip-insp__load-more');

    function showItems(upTo) {
      allItems.forEach(function (item, idx) {
        if (idx < upTo) {
          item.removeAttribute('hidden');
        } else {
          item.setAttribute('hidden', '');
        }
      });
      shownCount = Math.min(upTo, allItems.length);

      if (loadMoreBtn) {
        if (shownCount < allItems.length) {
          loadMoreBtn.removeAttribute('hidden');
        } else {
          loadMoreBtn.setAttribute('hidden', '');
        }
      }
    }

    /* Initial render */
    showItems(initialCount);

    if (showLoadMore && loadMoreBtn) {
      loadMoreBtn.addEventListener('click', function () {
        showItems(shownCount + 20);
      });
    }

    /* ----------------------------------------------------------------
       LIGHTBOX — horizontal carousel
       Navigation: prev/next buttons, swipe L/R, ArrowLeft/Right keys
       Close: X button, Esc key, click on overlay outside image
    ---------------------------------------------------------------- */
    if (!enableLightbox) return;

    var lightbox    = document.querySelector('.vip-insp__lightbox');
    var closeBtn    = document.querySelector('.vip-insp__lightbox-close');
    var track       = document.querySelector('.vip-insp__lightbox-track');
    var slides      = document.querySelectorAll('.vip-insp__slide');
    var prevBtn     = document.querySelector('.vip-insp__lightbox-prev');
    var nextBtn     = document.querySelector('.vip-insp__lightbox-next');
    var counterEl   = document.querySelector('.vip-insp__lightbox-counter');

    if (!lightbox || !track || !slides.length) return;

    var totalSlides  = slides.length;
    var currentSlide = 0;
    var savedScrollY = 0;

    /* ---- Body scroll lock ---- */
    function lockBodyScroll() {
      savedScrollY = window.scrollY || window.pageYOffset;
      document.body.style.position = 'fixed';
      document.body.style.top      = '-' + savedScrollY + 'px';
      document.body.style.left     = '0';
      document.body.style.right    = '0';
      document.body.style.overflow = 'hidden';
    }

    function unlockBodyScroll() {
      document.body.style.position = '';
      document.body.style.top      = '';
      document.body.style.left     = '';
      document.body.style.right    = '';
      document.body.style.overflow = '';
      window.scrollTo(0, savedScrollY);
    }

    /* ---- Update track position and counter ---- */
    function goToSlide(idx, animate) {
      /* Clamp index */
      idx = Math.max(0, Math.min(idx, totalSlides - 1));
      currentSlide = idx;

      /* Horizontal translate — each slide is 100vw wide */
      var offset = -idx * 100;
      if (animate === false || prefersReducedMotion) {
        track.style.transition = 'none';
        track.style.transform  = 'translate3d(' + offset + '%, 0, 0)';
        /* Re-enable transition after paint */
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            track.style.transition = '';
          });
        });
      } else {
        track.style.transform = 'translate3d(' + offset + '%, 0, 0)';
      }

      /* Counter: "3 / 12" */
      if (counterEl) {
        counterEl.textContent = (idx + 1) + ' / ' + totalSlides;
      }

      /* Prev/next arrow visibility */
      if (prevBtn) prevBtn.style.opacity = idx === 0 ? '0.3' : '1';
      if (nextBtn) nextBtn.style.opacity = idx === totalSlides - 1 ? '0.3' : '1';
    }

    /* ---- Open / close ---- */
    function openLightbox(idx) {
      lockBodyScroll();
      lightbox.removeAttribute('hidden');
      lightbox.focus();
      goToSlide(idx, false); /* No animation on open — instant */
    }

    function closeLightbox() {
      lightbox.setAttribute('hidden', '');
      unlockBodyScroll();
    }

    /* ---- Open on gallery item click ---- */
    allItems.forEach(function (item) {
      item.addEventListener('click', function (e) {
        e.preventDefault();
        var idx = parseInt(item.dataset.inspIdx, 10) || 0;
        openLightbox(idx);
      });
    });

    /* ---- Close button ---- */
    if (closeBtn) {
      closeBtn.addEventListener('click', closeLightbox);
    }

    /* ---- Prev / next arrows ---- */
    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        goToSlide(currentSlide - 1);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        goToSlide(currentSlide + 1);
      });
    }

    /* ---- Keyboard: ArrowLeft, ArrowRight, Esc ---- */
    document.addEventListener('keydown', function (e) {
      if (lightbox.hasAttribute('hidden')) return;
      if (e.key === 'Escape' || e.keyCode === 27) {
        closeLightbox();
      } else if (e.key === 'ArrowLeft' || e.keyCode === 37) {
        goToSlide(currentSlide - 1);
      } else if (e.key === 'ArrowRight' || e.keyCode === 39) {
        goToSlide(currentSlide + 1);
      }
    });

    /* ---- Click on dark overlay (outside slide area) closes lightbox ---- */
    lightbox.addEventListener('click', function (e) {
      /* Only close if click is directly on lightbox backdrop, not on children */
      if (e.target === lightbox) {
        closeLightbox();
      }
    });

    /* ----------------------------------------------------------------
       TOUCH SWIPE — horizontal, left/right navigation
       Threshold: 50px horizontal travel with < 80px vertical drift
    ---------------------------------------------------------------- */
    var touchStartX  = 0;
    var touchStartY  = 0;
    var touchMoveX   = 0;
    var isSwiping    = false;

    lightbox.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return;
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      touchMoveX  = 0;
      isSwiping   = true;
    }, { passive: true });

    lightbox.addEventListener('touchmove', function (e) {
      if (!isSwiping || e.touches.length !== 1) return;
      touchMoveX = e.touches[0].clientX - touchStartX;
      var moveY  = e.touches[0].clientY - touchStartY;

      /* If user is scrolling vertically, cancel horizontal swipe */
      if (Math.abs(moveY) > Math.abs(touchMoveX) + 10) {
        isSwiping = false;
        return;
      }

      /* Live drag feedback: offset the track slightly */
      var baseOffset = -currentSlide * 100;
      var dragPercent = (touchMoveX / window.innerWidth) * 100;
      if (!prefersReducedMotion) {
        track.style.transition = 'none';
        track.style.transform  = 'translate3d(' + (baseOffset + dragPercent) + '%, 0, 0)';
      }
    }, { passive: true });

    lightbox.addEventListener('touchend', function (e) {
      if (!isSwiping) return;
      isSwiping = false;

      var deltaX = e.changedTouches[0].clientX - touchStartX;
      var deltaY = e.changedTouches[0].clientY - touchStartY;

      /* Ignore if mostly vertical */
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        /* Snap back to current slide */
        goToSlide(currentSlide, false);
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            track.style.transition = '';
          });
        });
        return;
      }

      /* Re-enable transition then navigate */
      track.style.transition = '';

      if (deltaX < -50 && currentSlide < totalSlides - 1) {
        /* Swipe left → next */
        goToSlide(currentSlide + 1);
      } else if (deltaX > 50 && currentSlide > 0) {
        /* Swipe right → prev */
        goToSlide(currentSlide - 1);
      } else {
        /* Not far enough — snap back */
        goToSlide(currentSlide);
      }
    }, { passive: true });

  }

  /* ---- Bootstrap ---- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
